// sim/casteGenes.ts — CASTE↔GENE COMPATIBILITY: a PURE, CLASSIFICATION-ONLY read-model
// that answers "which castes does this body-part gene actually suit?".
//
// THE PROBLEM it solves: every gene type drops for every caste (CASTE_TYPE_WEIGHT has no
// zero column), so a player cannot tell, at a glance, WHO a looted organ is for. This
// layer reads the SAME affinity table the loot forge already uses and distils it into a
// small, deterministic set of VALID castes per gene type — so loot can advertise "this is
// a Soldier gene" / "Worker + Flyer (Hybrid)" and players chase upgrades for a caste.
//
// CRITICAL — this is NOT a tick System, and NOT sim state (mirrors src/sim/advisor.ts,
// src/sim/diagnostics.ts and src/sim/genePower.ts, the render-only layers in DESIGN.md
// §19/§24):
//   • It NEVER mutates state, draws RNG, or runs in the GameLoop.
//   • It is NOT folded into any state hash and is NOT part of the save payload. Compute
//     it before or after a tick and the world hash is byte-identical (replay-safe).
//   • It is a pure FUNCTION over the static, already-deterministic CASTE_TYPE_WEIGHT
//     table — same gene type ⇒ identical caste set, always.
//   • Returning a small fresh array/string per call is fine: these are display read-models
//     called on demand (web gene-inventory render, loot labels, tests), NOT the per-tick
//     hot path. (The set per gene type is fixed; a caller that wants zero allocation can
//     cache the six results once.)
//
// DESIGN of the threshold (asserted in casteGenes.test.mjs): a gene type is VALID for a
// caste when that caste's affinity weight for it clears CASTE_VALID_THRESHOLD. At the
// chosen value (6) over the shipped table this yields, by construction:
//   Wing       → Flyer
//   Mandible   → Soldier
//   VenomGland → Specialist
//   Carapace   → Soldier
//   Antenna    → Worker + Flyer   (HYBRID — both sense-heavy castes use antennae)
//   Limb       → Worker + Specialist (HYBRID — both ground forage-castes use limbs)
// Two HYBRID types fall out naturally, matching the lore intent that some organs serve
// more than one role.

import { CASTE_COUNT, GENE_TYPE_COUNT } from '../types.ts';
import { CASTE_TYPE_WEIGHT } from './geneData.ts';

// ---------------------------------------------------------------------------
// Caste labels + glyphs (index-aligned with CASTE.*: Worker, Soldier, Flyer,
// Specialist) — now sourced from the canonical `casteNames` module and RE-EXPORTED
// here so this module's existing importers (compatibility chips) are unaffected.
// ---------------------------------------------------------------------------

export { CASTE_NAME, CASTE_GLYPH } from './casteNames.ts';
import { CASTE_NAME, CASTE_GLYPH } from './casteNames.ts';

// ---------------------------------------------------------------------------
// The compatibility threshold.
// ---------------------------------------------------------------------------

/**
 * A gene type is VALID for a caste when `CASTE_TYPE_WEIGHT[caste][type] >= this`.
 * Tuned to the shipped affinity table so each type resolves to its dominant
 * caste(s) and exactly two types (Antenna, Limb) come out as HYBRIDs. See the
 * file header + casteGenes.test.mjs (which pins the resulting per-type sets).
 */
export const CASTE_VALID_THRESHOLD = 6;

// ---------------------------------------------------------------------------
// One compact part of a compatibility chip (glyph + caste name), for UIs that
// want to render each caste distinctly rather than a single joined string.
// ---------------------------------------------------------------------------

export interface CasteChip {
  /** Caste index (CASTE.*). */
  caste: number;
  /** Caste display name. */
  name: string;
  /** Compact caste glyph. */
  glyph: string;
}

// ---------------------------------------------------------------------------
// Core classification.
// ---------------------------------------------------------------------------

/** True when `geneType` is a real index into CASTE_TYPE_WEIGHT's columns. */
function inRange(geneType: number): boolean {
  return Number.isInteger(geneType) && geneType >= 0 && geneType < GENE_TYPE_COUNT;
}

/** Affinity weight of a caste for a gene type, or 0 if either index is out of range. */
function weightAt(caste: number, geneType: number): number {
  const row = CASTE_TYPE_WEIGHT[caste];
  if (row === undefined) return 0;
  const w = row[geneType];
  return w === undefined ? 0 : w;
}

/**
 * The castes (ascending CASTE.* index) whose affinity for `geneType` clears
 * CASTE_VALID_THRESHOLD — i.e. the castes this body part actually suits.
 *
 * GUARANTEE: never empty. If no caste clears the threshold (only possible if the
 * table is retuned, or for a partially-valid out-of-range query), it falls back to
 * the single argmax caste (highest weight; ties resolve to the LOWEST caste index),
 * so callers can always show at least one target. An unknown gene type (all weights
 * read as 0) degrades to `[0]` (Worker) via the same tie→lowest argmax rule rather
 * than throwing.
 *
 * Deterministic + allocation-light: one fixed forward scan, one small array out.
 */
/**
 * The single caste a gene type most suits — the argmax of its CASTE_TYPE_WEIGHT
 * column (highest affinity; ties resolve to the LOWEST caste index). Always a real
 * caste (every column has a max). The reward-resolution EQUIP action targets THIS
 * caste so a gene equips where it belongs in one click (Mandible→Soldier, Wing→Flyer,
 * Limb→Worker, …). Deterministic; one fixed forward scan.
 */
export function primaryCasteForType(geneType: number): number {
  let argmax = 0;
  let argmaxW = -1;
  for (let caste = 0; caste < CASTE_COUNT; caste++) {
    const w = weightAt(caste, geneType);
    // Strict `>` keeps the FIRST (lowest-index) caste on a tie — deterministic.
    if (w > argmaxW) { argmaxW = w; argmax = caste; }
  }
  return argmax;
}

export function validCastesForType(geneType: number): readonly number[] {
  const out: number[] = [];
  for (let caste = 0; caste < CASTE_COUNT; caste++) {
    if (weightAt(caste, geneType) >= CASTE_VALID_THRESHOLD) out.push(caste);
  }
  // Never empty: fall back to the single best-affinity caste (the argmax).
  if (out.length === 0) out.push(primaryCasteForType(geneType));
  return out;
}

/** True when `geneType` is valid for `caste` (agrees with validCastesForType). */
export function isValidForCaste(geneType: number, caste: number): boolean {
  const valid = validCastesForType(geneType);
  for (let i = 0; i < valid.length; i++) if (valid[i] === caste) return true;
  return false;
}

/** True when a gene type suits two or more castes (a HYBRID organ). */
export function isHybridType(geneType: number): boolean {
  return validCastesForType(geneType).length >= 2;
}

/**
 * Human compatibility label for a gene type, e.g. `"Soldier"` or
 * `"Worker + Flyer (Hybrid)"` — caste names joined with " + ", with " (Hybrid)"
 * appended when two or more castes qualify. Always non-empty.
 */
export function casteCompatLabel(geneType: number): string {
  const valid = validCastesForType(geneType);
  let label = '';
  for (let i = 0; i < valid.length; i++) {
    const name = CASTE_NAME[valid[i] as number] ?? `Caste ${valid[i] as number}`;
    label = i === 0 ? name : `${label} + ${name}`;
  }
  return valid.length >= 2 ? `${label} (Hybrid)` : label;
}

/**
 * The compatibility chip pieces (glyph + name per valid caste) for compact UI —
 * lets the renderer style each caste distinctly instead of parsing the joined
 * label. Order matches validCastesForType (ascending caste index).
 */
export function casteCompatChips(geneType: number): readonly CasteChip[] {
  const valid = validCastesForType(geneType);
  const chips: CasteChip[] = [];
  for (let i = 0; i < valid.length; i++) {
    const caste = valid[i] as number;
    chips.push({
      caste,
      name: CASTE_NAME[caste] ?? `Caste ${caste}`,
      glyph: CASTE_GLYPH[caste] ?? '•',
    });
  }
  return chips;
}

/**
 * True when `geneType` is a real GENE_TYPE index (a column of CASTE_TYPE_WEIGHT).
 * Exposed so callers can distinguish a genuine classification from the safe
 * out-of-range fallback (which degrades to the argmax caste rather than throwing).
 */
export { inRange as isKnownGeneType };
